import { Badge, Box, Card, Group, Stack, Text } from "@mantine/core";
import { useSnapshot } from "valtio";
import { assistantLogTexts } from "@/locales/assistant-log";
import { assistantLogDetailTexts } from "@/locales/assistant-log-detail";
import { i18nStore } from "@/store/i18n";
import type { AssistantLogMessageDto } from "@/types/ai-assistant-log";
import {
	formatLogCount,
	formatLogLatency,
	formatLogTime,
} from "./log-format.logic";
import { LogVendorBadge } from "./log-vendor-badge";

/**
 * Satu gelembung transkrip (lapis 3). Pesan pengguna rata kanan; jawaban Jenna
 * rata kiri dengan baris meta (vendor + model + latensi + token + tool).
 * Giliran yang dijawab profil Cadangan diberi chip kuning, giliran gagal
 * diberi kotak merah berikut catatan bahwa tokennya tidak terpakai.
 *
 * `content` null = isi disembunyikan (di luar retensi atau toggle admin);
 * yang tampil hanya metadata.
 */
export function LogTranscriptBubble({
	message,
}: {
	message: AssistantLogMessageDto;
}) {
	const { lang: uiLang } = useSnapshot(i18nStore);
	const lang = uiLang === "en" ? "en" : "id";
	const t = assistantLogTexts[lang];
	const td = assistantLogDetailTexts[lang];
	const isUser = message.role === "user";
	const isError = message.status === "error";
	const isFailover = message.slot === "chat_backup";

	return (
		<Group justify={isUser ? "flex-end" : "flex-start"} align="flex-start">
			<Stack gap={4} maw="80%" align={isUser ? "flex-end" : "flex-start"}>
				<Card
					withBorder
					radius="md"
					p="sm"
					bg={isError ? "red.0" : isUser ? "orange.0" : undefined}
					style={
						isError
							? { borderLeft: "3px solid var(--mantine-color-red-6)" }
							: undefined
					}
				>
					{message.content === null ? (
						<Text size="sm" c="dimmed" fs="italic">
							{td.contentHiddenNote}
						</Text>
					) : (
						<Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
							{message.content}
						</Text>
					)}
					{isError && (
						<Stack gap={2} mt="xs">
							<Text size="xs" c="red" fw={600}>
								{td.errorTurn(td.errorTurnDefault)}
							</Text>
							<Text size="xs" c="dimmed">
								{td.errorTurnNote}
							</Text>
						</Stack>
					)}
				</Card>

				<Group
					gap="xs"
					wrap="wrap"
					justify={isUser ? "flex-end" : "flex-start"}
				>
					<Text size="xs" c="dimmed">
						{formatLogTime(message.at, lang)}
					</Text>
					{isUser && message.pageRoute && (
						<Text size="xs" c="dimmed">
							· {td.askedFrom(message.pageRoute)}
						</Text>
					)}
					{!isUser && (
						<>
							<LogVendorBadge
								use={{
									model: message.model,
									vendor: message.vendor,
									turns: 0,
								}}
							/>
							<Text size="xs" c="dimmed">
								{formatLogLatency(message.latencyMs, lang)}
							</Text>
							<Text size="xs" c="dimmed">
								{formatLogCount(message.inputTokens, lang)} in /{" "}
								{formatLogCount(message.outputTokens, lang)} out
							</Text>
							{isFailover && (
								<Badge color="yellow" variant="light" size="sm">
									{td.failoverChip}
								</Badge>
							)}
							{message.modality === "voice" && (
								<Badge color="grape" variant="light" size="sm">
									{t.modalityVoice}
								</Badge>
							)}
						</>
					)}
				</Group>

				{message.toolsUsed.length > 0 && (
					<Group gap={4} wrap="wrap">
						{message.toolsUsed.map((tool) => (
							<Badge key={tool} variant="outline" size="xs" ff="monospace">
								{tool}
							</Badge>
						))}
					</Group>
				)}
				<Box />
			</Stack>
		</Group>
	);
}
